import { getApiErrorMessage } from './apiError';

/** Debe coincidir con [RequestSizeLimit] en PreciosProveedorController (API). */
export const MAX_EXCEL_IMPORT_BYTES = 50_000_000;

/**
 * Tamaño legible en MB/KB decimales (1 MB = 1.000.000 bytes), alineado al límite del servidor.
 */
export function formatExcelFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1_000) return `${bytes} B`;
  if (bytes < 1_000_000) {
    return `${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 1 }).format(bytes / 1_000)} KB`;
  }
  return `${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 1 }).format(bytes / 1_000_000)} MB`;
}

/** Etiqueta del tope (misma unidad que formatExcelFileSize). */
export const MAX_EXCEL_IMPORT_LABEL = formatExcelFileSize(MAX_EXCEL_IMPORT_BYTES);

export const EXCEL_IMPORT_SIZE_HELPER_TEXT =
  `Tamaño máximo del archivo: ${MAX_EXCEL_IMPORT_LABEL} (${MAX_EXCEL_IMPORT_BYTES.toLocaleString('es-AR')} bytes). ` +
  'Usá solo la hoja y el rango de filas que vas a importar; libros con muchas hojas, imágenes o formatos pesados pueden superar el límite aunque tengan pocas filas de datos.';

export function isExcelImportTooLarge(file: File | null | undefined): boolean {
  return !!file && file.size > MAX_EXCEL_IMPORT_BYTES;
}

export function getExcelFileTooLargeMessage(file: File): string {
  return (
    `El archivo pesa ${formatExcelFileSize(file.size)} y supera el límite de ${MAX_EXCEL_IMPORT_LABEL} ` +
    `(${MAX_EXCEL_IMPORT_BYTES.toLocaleString('es-AR')} bytes). ` +
    'Reducí el Excel: dejá solo la hoja y las filas que vas a importar, sin imágenes ni hojas extra.'
  );
}

/** Texto bajo el nombre del archivo (dentro / fuera del límite). */
export function getExcelFileSizeStatusText(file: File): string {
  const tam = formatExcelFileSize(file.size);
  if (isExcelImportTooLarge(file)) {
    return `Tamaño: ${tam} — supera el límite de ${MAX_EXCEL_IMPORT_LABEL}. No se puede simular ni procesar.`;
  }
  return `Tamaño: ${tam} — dentro del límite (${MAX_EXCEL_IMPORT_LABEL}).`;
}

type ImportExcelErrBody = {
  message?: string;
  detail?: string;
  inner?: string;
  traceId?: string;
  maxBytes?: number;
  errors?: Record<string, string[] | string>;
};

const IMPORT_FIELD_LABELS: Record<string, string> = {
  nombrehoja: 'Hoja del Excel',
  primerafiladatos: 'Primera fila de datos',
  ultimafiladatos: 'Hasta fila',
  columnacodigoarticulo: 'Columna código de artículo',
  columnacosto: 'Columna costo base',
  columnaprecionetaventa: 'Columna precio neto de venta',
  dryrun: 'Modo simulación',
  guardarhistorico: 'Guardar histórico',
  archivo: 'Archivo Excel',
};

function normalizeImportFieldKey(key: string): string {
  return key.replace(/^(\$|\.)+/, '').toLowerCase();
}

function labelForImportField(key: string): string {
  const norm = normalizeImportFieldKey(key);
  return IMPORT_FIELD_LABELS[norm] ?? key;
}

/** Convierte el diccionario `errors` de la API en texto legible para el usuario. */
function formatImportValidationErrors(errors: Record<string, string[] | string>): string {
  const lines: string[] = [];

  for (const [rawKey, rawVal] of Object.entries(errors)) {
    const label = labelForImportField(rawKey);
    const msgs = (Array.isArray(rawVal) ? rawVal : [rawVal]).filter(
      (m): m is string => typeof m === 'string' && m.trim().length > 0
    );

    if (msgs.length === 0) {
      lines.push(`${label}: valor inválido o faltante.`);
      continue;
    }

    msgs.forEach((m) => lines.push(`${label}: ${m.trim()}`));
  }

  return lines.join(' ');
}

/**
 * Mensaje claro para el usuario al fallar Simular/Procesar (413, red, API).
 */
export function resolveImportExcelError(
  error: unknown,
  file: File | null | undefined,
  defaultMessage: string
): string {
  const err = error as { response?: { status?: number; data?: ImportExcelErrBody }; request?: unknown };
  const status = err.response?.status;

  if (status === 413) {
    if (file) {
      return getExcelFileTooLargeMessage(file);
    }
    const data = err.response?.data;
    const maxLabel =
      data?.maxBytes && Number.isFinite(data.maxBytes)
        ? formatExcelFileSize(data.maxBytes)
        : MAX_EXCEL_IMPORT_LABEL;
    return data?.message?.trim() || `El archivo supera el tamaño máximo permitido (${maxLabel}).`;
  }

  if (!err.response) {
    if (file && isExcelImportTooLarge(file)) {
      return getExcelFileTooLargeMessage(file);
    }
    if (err.request) {
      return 'No se pudo conectar con el servidor. Verifique su conexión e intente de nuevo.';
    }
  }

  const data = err.response?.data;
  if (data && typeof data === 'object') {
    const validationText =
      data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)
        ? formatImportValidationErrors(data.errors)
        : '';

    const serverDetail = [data.detail, data.inner].filter(
      (p): p is string => typeof p === 'string' && p.trim().length > 0
    );

    if (validationText) {
      let msg = validationText;
      if (serverDetail.length > 0) {
        msg += ` — ${serverDetail.join(' — ')}`;
      }
      if (data.traceId) msg += ` [trace: ${data.traceId}]`;
      return msg;
    }

    const parts = [data.message, data.detail, data.inner].filter(
      (p): p is string => typeof p === 'string' && p.trim().length > 0
    );
    if (parts.length > 0) {
      let msg = parts.join(' — ');
      if (data.traceId) msg += ` [trace: ${data.traceId}]`;
      return msg;
    }
  }

  const apiMsg = getApiErrorMessage(error, '');
  if (apiMsg) return apiMsg;

  if (status === 400) {
    return `${defaultMessage} Revise hoja, columnas y archivo Excel.`;
  }

  return defaultMessage;
}
